/**
 * NTN Verify Hub — Operations Dashboard (internal, staff-only).
 * The heartbeat of the verification team: three track summary cards, a
 * workload table by verifier, and the supervisor metrics the June 2026
 * audit called for (avg turnaround, oldest case, avg contact attempts).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { DashboardSkeleton } from "@/components/Skeleton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Briefcase, Home, FileText, AlertTriangle, Users, Clock,
  ArrowRight, Gauge, ListTodo, PhoneCall,
} from "lucide-react";

const TRACK_ICON = { employment: Briefcase, landlord: Home, documents: FileText };
const TRACK_ACCENT = {
  employment: "bg-blue-50 text-blue-700 border-blue-200",
  landlord: "bg-emerald-50 text-emerald-700 border-emerald-200",
  documents: "bg-amber-50 text-amber-700 border-amber-200",
};

function fmtSeconds(sec) {
  if (!sec || sec <= 0) return "—";
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
}

function StatTile({ label, value, testid, tone = "default" }) {
  const tones = {
    default: "text-slate-900",
    danger: "text-rose-700",
    warn: "text-amber-700",
    good: "text-emerald-700",
  };
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tones[tone] || tones.default}`}>{value}</div>
    </div>
  );
}

function TrackCard({ card, onOpen }) {
  const Icon = TRACK_ICON[card.track] || Briefcase;
  return (
    <Card
      className={`p-6 border ${TRACK_ACCENT[card.track] || ""} bg-white cursor-pointer hover:shadow-md transition`}
      onClick={onOpen}
      data-testid={`ops-track-card-${card.track}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-md flex items-center justify-center ${TRACK_ACCENT[card.track]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Verification Queue</div>
            <div className="ntn-h3 text-slate-900">{card.label}</div>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400 shrink-0 mt-2" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <StatTile label="Total open" value={card.total_open} testid={`ops-${card.track}-total-open`} />
        <StatTile label="Assigned to me" value={card.assigned_to_me} testid={`ops-${card.track}-mine`} tone="good" />
        <StatTile label="Unassigned" value={card.unassigned} testid={`ops-${card.track}-unassigned`} tone={card.unassigned > 0 ? "warn" : "default"} />
        <StatTile label="Awaiting response" value={card.awaiting_response} testid={`ops-${card.track}-awaiting`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-slate-100">
        <StatTile label="Needs review" value={card.needs_review} testid={`ops-${card.track}-review`} />
        <StatTile label="Completed today" value={card.completed_today} testid={`ops-${card.track}-completed`} tone="good" />
        <StatTile label="Overdue" value={card.overdue} testid={`ops-${card.track}-overdue`} tone={card.overdue > 0 ? "danger" : "default"} />
        <StatTile
          label="Oldest open"
          value={card.oldest_case?.applicant_name ? fmtSeconds(
            (Date.now() - new Date(card.oldest_case.started_at).getTime()) / 1000
          ) : "—"}
          testid={`ops-${card.track}-oldest`}
        />
      </div>
    </Card>
  );
}

export default function OperationsDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [caseCounts, setCaseCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role !== "ntn_staff") { navigate("/dashboard"); return; }
    apiClient.get("/staff-ops/dashboard")
      .then(({ data }) => setData(data))
      .catch(() => setData({ tracks: [], supervisor_metrics: {} }))
      .finally(() => setLoading(false));
    apiClient.get("/staff-ops/cases", { params: { limit: 1 } })
      .then(({ data }) => setCaseCounts(data.counts || null))
      .catch(() => setCaseCounts(null));
  }, [user, navigate]);

  if (loading) return <AppShell><DashboardSkeleton /></AppShell>;
  if (!data) return null;

  const sm = data.supervisor_metrics || {};

  return (
    <AppShell>
      <div className="space-y-8" data-testid="operations-dashboard">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-overline text-slate-500 mb-2">NTN Staff · Operations</div>
            <h1 className="ntn-h1">Verification Operations</h1>
            <p className="ntn-body mt-2 text-slate-600 max-w-2xl">
              The operational heartbeat of the verification team. Real-time queues, workload by verifier, and the metrics supervisors need to keep every case moving.
            </p>
          </div>
        </div>

        {/* Phase F — main Case Queue entry */}
        <Card
          className="p-5 bg-slate-900 text-white border-slate-800 cursor-pointer hover:bg-slate-800 transition-colors"
          onClick={() => navigate("/staff-ops/cases")}
          data-testid="ops-case-queue-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center">
                <ListTodo className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Main working queue</div>
                <div className="ntn-h3">NTN Case Queue</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {caseCounts && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className={caseCounts.new > 0 ? "text-rose-300 font-bold" : "text-slate-400"} data-testid="ops-case-count-new">
                    New ({caseCounts.new ?? 0})
                  </span>
                  <span className="text-slate-300" data-testid="ops-case-count-review">Needs Review ({caseCounts.needs_review ?? 0})</span>
                  <span className="text-slate-300" data-testid="ops-case-count-progress">In Progress ({caseCounts.in_progress ?? 0})</span>
                  <span className="text-slate-300" data-testid="ops-case-count-waiting">Waiting ({caseCounts.waiting ?? 0})</span>
                </div>
              )}
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          </div>
        </Card>

        {/* Track summary cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {data.tracks?.map((t) => (
            <TrackCard
              key={t.track}
              card={t}
              onOpen={() => navigate(`/staff-ops/${t.track}`)}
            />
          ))}
        </div>

        {/* Supervisor metrics */}
        <Card className="p-6 bg-white border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="h-5 w-5 text-slate-500" />
            <div className="ntn-h3 text-slate-900">Supervisor metrics</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatTile label="Avg turnaround" value={fmtSeconds(sm.avg_turnaround_seconds)} testid="ops-metric-avg-turnaround" />
            <StatTile label="Avg open-case age" value={fmtSeconds(sm.avg_open_case_age_seconds)} testid="ops-metric-avg-age" />
            <StatTile label="Total overdue" value={sm.total_overdue || 0} testid="ops-metric-overdue" tone={sm.total_overdue > 0 ? "danger" : "default"} />
            <StatTile label="Avg contact attempts" value={sm.avg_contact_attempts || 0} testid="ops-metric-avg-attempts" />
          </div>
          {sm.workload_by_verifier?.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Workload by verifier</div>
              <div className="border border-slate-200 rounded-md divide-y" data-testid="ops-workload-table">
                {sm.workload_by_verifier.map((w) => (
                  <div key={w.staff_id} className="flex items-center gap-4 px-4 py-2.5 text-sm">
                    <Users className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="flex-1 font-medium text-slate-800 truncate">{w.staff_name}</div>
                    <div className="w-24 text-right"><span className="text-slate-500 text-xs mr-1">Open</span>{w.open_count}</div>
                    <div className="w-32 text-right"><span className="text-slate-500 text-xs mr-1">Awaiting</span>{w.awaiting_count}</div>
                    <div className={`w-28 text-right ${w.overdue_count > 0 ? "text-rose-700 font-semibold" : "text-slate-500"}`}>
                      <span className="text-xs mr-1">Overdue</span>{w.overdue_count}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Quick nav */}
        <div className="flex flex-wrap gap-3">
          {["employment", "landlord", "documents"].map((t) => (
            <Button
              key={t}
              variant="outline"
              className="rounded-md"
              onClick={() => navigate(`/staff-ops/${t}`)}
              data-testid={`ops-open-${t}-queue`}
            >
              <ListTodo className="h-4 w-4 mr-2" />
              {t.charAt(0).toUpperCase() + t.slice(1)} queue
            </Button>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
